// noinspection GraphQLUnresolvedReference

import { useMutation } from '@tanstack/react-query'

import type { GraphqlClientOptionsProps } from '../../../../base/api/graphqlClient.ts'
import { graphqlClient, toGraphQlError } from '../../../../base/api/graphqlClient.ts'
import type { MutationOpts } from '../../../../base/types/reactQuery.ts'
import type { EntidadInputProps } from '../../../../interfaces'
import { gqlAst } from '../../../../utils/gql.ts'
import type { WaapiEnviarUrlInputProps, WaapiEnviarUrlProps } from '../interfaces/waapi.ts'

const query = /* GraphQL */ gqlAst`
  mutation WAAPI_ENVIAR_URL($entidad: EntidadParamsInput!, $input: WaapiEnviarUrlInput!) {
    waapiEnviarUrl(entidad: $entidad, input: $input) {
      _id
      codigoArea
      codigoPuntoVenta
      codigoSucursal
      createdAt
      glosa
      mediaName
      mediaUrl
      mensaje
      state
      telefono
      updatedAt
      usucre
      usumod
      waapiStatus
    }
  }
`

export const MUTATION_KEY_WAAPI_ENVIAR_URL = ['waapi-enviar-url'] as const

export interface ApiWaapiEnviarUrlParams {
  /** Sucursal y punto de venta de la sesión */
  entidad: EntidadInputProps
  /** Teléfono, mensaje y archivo (url) a enviar */
  input: WaapiEnviarUrlInputProps
}

/**
 * Envía un archivo por WhatsApp (Waapi) a partir de su url, ej. el PDF de una factura.
 * @param params Entidad y datos del envío (teléfono, mensaje, nombre y url del archivo).
 * @param options Headers adicionales y señal de cancelación de la petición.
 * @returns El registro del envío con su `waapiStatus`.
 */
export const apiWaapiEnviarUrl = async (
  params: ApiWaapiEnviarUrlParams,
  options: GraphqlClientOptionsProps = {},
): Promise<WaapiEnviarUrlProps> => {
  try {
    const data = await graphqlClient.request<{ waapiEnviarUrl: WaapiEnviarUrlProps }>({
      document: query,
      variables: { ...params },
      requestHeaders: options.headers,
      signal: options.signal,
    })
    return data.waapiEnviarUrl
  } catch (error) {
    throw toGraphQlError(error, options.signal)
  }
}

/**
 * Hook de mutación para enviar un archivo por WhatsApp (Waapi) a partir de su url.
 * @example const { mutateAsync } = useWaapiEnviarUrl({ onError: showError })
 * @param options Opciones de React Query (onSuccess, onError, ...).
 */
export const useWaapiEnviarUrl = (options?: MutationOpts<WaapiEnviarUrlProps, ApiWaapiEnviarUrlParams>) => {
  return useMutation({
    mutationKey: MUTATION_KEY_WAAPI_ENVIAR_URL,
    ...options,
    mutationFn: (params: ApiWaapiEnviarUrlParams) => apiWaapiEnviarUrl(params),
  })
}
